// DeletedView — the "trash drawer" surfaced via the chip in CardsPanel
// header. Lists every card whose latest history op is `delete`.
//
// Default grouping (the most useful split for triage):
//   - Agent edits / User deletes — "did the agent delete content I cared
//     about?" / "did I delete this on purpose?". Both expanded by default.
//   - Rejected gaps / Resolved gaps — by far the most numerous in normal
//     dogfood (every Reject and every Resolve becomes one). Collapsed by
//     default so they don't drown the audit signal.
//   - fs deletes — cards removed via `rm` outside the app. Rare but
//     useful evidence when a teammate's git pull dropped something.
//
// Each entry shows op chip + author + when + delete_reason + a body
// preview. Click "view full history" to open a modal with the full
// revision list (including the delete row's snapshot).

import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api, type CardHistoryRevision } from '../api';
import i18n from '../i18n';
import { HistoryDrawer } from './cards/HistoryDrawer';
import { specForKind } from '../kinds';

export interface DeletedViewProps {
  projectId: string;
  /** Switch back to the cards view. */
  onClose: () => void;
}

type GroupKey = 'agent_edit' | 'user_delete' | 'rejected_gap' | 'resolved_gap' | 'fs_delete' | 'other';

export function DeletedView({ projectId, onClose }: DeletedViewProps) {
  const { t } = useTranslation();
  const [rows, setRows] = useState<CardHistoryRevision[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [openHistoryFor, setOpenHistoryFor] = useState<string | null>(null);
  const [collapsed, setCollapsed] = useState<Record<GroupKey, boolean>>({
    agent_edit: false,
    user_delete: false,
    rejected_gap: true,
    resolved_gap: true,
    fs_delete: false,
    other: false,
  });

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    api
      .listDeletedCards(projectId)
      .then((rs) => {
        if (cancelled) return;
        setRows(rs);
      })
      .catch((e) => {
        if (cancelled) return;
        setError((e as Error).message);
      })
      .finally(() => {
        if (cancelled) return;
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [projectId]);

  const grouped = useMemo(() => {
    const groups: Record<GroupKey, CardHistoryRevision[]> = {
      agent_edit: [],
      user_delete: [],
      rejected_gap: [],
      resolved_gap: [],
      fs_delete: [],
      other: [],
    };
    for (const row of rows) {
      groups[bucketFor(row)].push(row);
    }
    return groups;
  }, [rows]);

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        minHeight: 0,
        background: 'white',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          padding: '10px 14px',
          borderBottom: '1px solid var(--rule)',
          background: 'var(--paper-0)',
          flexShrink: 0,
        }}
      >
        <span
          className="mono"
          style={{ fontSize: 11, fontWeight: 700, letterSpacing: '.1em', color: 'var(--ink-0)' }}
        >
          {t('deleted.heading')} · {rows.length}
        </span>
        <span style={{ flex: 1 }} />
        <button
          onClick={onClose}
          className="mono"
          style={{
            fontSize: 11,
            padding: '4px 10px',
            border: '1px solid var(--rule)',
            borderRadius: 4,
            background: 'white',
            color: 'var(--ink-1)',
            fontWeight: 600,
            letterSpacing: '.04em',
            cursor: 'pointer',
          }}
        >
          ← {t('deleted.backToCards')}
        </button>
      </div>

      <div style={{ flex: 1, overflowY: 'auto', padding: '10px 0 16px' }}>
        {loading && (
          <div style={{ padding: '20px 16px', color: 'var(--ink-3)', fontSize: 12 }}>
            {t('common.loading')}
          </div>
        )}
        {error && (
          <div
            style={{
              padding: '20px 16px',
              color: 'var(--red)',
              fontSize: 12,
              fontFamily: 'JetBrains Mono, monospace',
            }}
          >
            {error}
          </div>
        )}
        {!loading && !error && rows.length === 0 && (
          <div style={{ padding: '20px 16px', color: 'var(--ink-3)', fontSize: 12 }}>
            {t('deleted.emptyState')}
          </div>
        )}

        {GROUP_ORDER.map((key) => {
          const list = grouped[key];
          if (list.length === 0) return null;
          return (
            <Group
              key={key}
              title={t(GROUP_LABEL_KEY[key])}
              count={list.length}
              collapsed={collapsed[key]}
              onToggle={() =>
                setCollapsed((prev) => ({ ...prev, [key]: !prev[key] }))
              }
            >
              {list.map((row) => (
                <DeletedRow
                  key={row.card_id}
                  row={row}
                  onShowHistory={() => setOpenHistoryFor(row.card_id)}
                />
              ))}
            </Group>
          );
        })}
      </div>

      {openHistoryFor && (
        <div
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.2)', zIndex: 1599 }}
          onClick={() => setOpenHistoryFor(null)}
        >
          <HistoryDrawer
            projectId={projectId}
            cardId={openHistoryFor}
            onClose={() => setOpenHistoryFor(null)}
            onRestored={() => {
              // Undelete recreates the cards row. Refetch the deleted list
              // so the restored card disappears from this view; the card
              // also reappears in the live cards panel via SSE on the
              // server's CardWritten broadcast.
              api
                .listDeletedCards(projectId)
                .then((rs) => setRows(rs))
                .catch(() => {});
              setOpenHistoryFor(null);
            }}
          />
        </div>
      )}
    </div>
  );
}

function bucketFor(row: CardHistoryRevision): GroupKey {
  if (row.author === 'fs') return 'fs_delete';
  const reason = row.delete_reason ?? '';
  if (reason.startsWith('rejected')) return 'rejected_gap';
  if (reason.startsWith('resolved')) return 'resolved_gap';
  if (row.author === 'agent') return 'agent_edit';
  if (row.author === 'user') return 'user_delete';
  return 'other';
}

const GROUP_ORDER: GroupKey[] = [
  'agent_edit',
  'user_delete',
  'rejected_gap',
  'resolved_gap',
  'fs_delete',
  'other',
];

/** i18n key per group bucket — resolved at render time. */
const GROUP_LABEL_KEY: Record<GroupKey, string> = {
  agent_edit: 'deleted.groupAgentEdit',
  user_delete: 'deleted.groupUserDelete',
  rejected_gap: 'deleted.groupRejectedGap',
  resolved_gap: 'deleted.groupResolvedGap',
  fs_delete: 'deleted.groupFsDelete',
  other: 'deleted.groupOther',
};

function Group({
  title,
  count,
  collapsed,
  onToggle,
  children,
}: {
  title: string;
  count: number;
  collapsed: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}) {
  return (
    <section style={{ marginBottom: 14 }}>
      <button
        onClick={onToggle}
        style={{
          all: 'unset',
          cursor: 'pointer',
          width: '100%',
          padding: '6px 16px',
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          color: 'var(--ink-3)',
        }}
      >
        <span className="mono" style={{ fontSize: 9.5, letterSpacing: '.12em', fontWeight: 700 }}>
          {collapsed ? '▸' : '▾'} {title.toUpperCase()}
        </span>
        <span className="mono" style={{ fontSize: 10 }}>
          · {count}
        </span>
      </button>
      {!collapsed && (
        <div style={{ padding: '4px 14px 0' }}>{children}</div>
      )}
    </section>
  );
}

function DeletedRow({
  row,
  onShowHistory,
}: {
  row: CardHistoryRevision;
  onShowHistory: () => void;
}) {
  const { t } = useTranslation();
  const spec = row.kind ? specForKind(row.kind) : null;
  const authorLabel =
    row.author === 'agent'
      ? t('deleted.byAgent')
      : row.author === 'user'
        ? t('deleted.byYou')
        : t('deleted.byFs');

  return (
    <article
      style={{
        margin: '0 0 8px',
        padding: '10px 12px',
        background: 'white',
        border: '1px solid var(--rule)',
        borderLeft: spec ? `3px solid ${spec.rule}` : '3px solid var(--rule)',
        borderRadius: 4,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
        {spec && (
          <span
            className="mono"
            style={{
              fontSize: 10,
              color: spec.color,
              letterSpacing: '.1em',
              fontWeight: 700,
            }}
          >
            {spec.glyph} {spec.label}
          </span>
        )}
        <span className="mono" style={{ fontSize: 10, color: 'var(--ink-3)' }}>
          {row.card_id}
        </span>
        <span style={{ flex: 1 }} />
        <span
          className="mono"
          style={{ fontSize: 9.5, color: 'var(--ink-3)', letterSpacing: '.06em', fontWeight: 600 }}
        >
          {authorLabel}
        </span>
        <span className="mono" style={{ fontSize: 10, color: 'var(--ink-3)' }}>
          {formatDeletedAt(row.created_at)}
        </span>
      </div>

      {row.title && (
        <div
          style={{
            fontSize: 13.5,
            fontWeight: 600,
            color: 'var(--ink-0)',
            lineHeight: 1.35,
            textDecoration: 'line-through',
            textDecorationColor: 'var(--ink-3)',
            marginBottom: 4,
          }}
        >
          {row.title}
        </div>
      )}

      {row.delete_reason && (
        <div
          style={{
            fontSize: 11.5,
            color: row.delete_reason.startsWith('rejected') ? 'var(--red)' : 'var(--ink-2)',
            lineHeight: 1.5,
            marginBottom: 4,
            fontStyle: 'italic',
          }}
        >
          {row.delete_reason}
        </div>
      )}

      {row.source_message_snippet && (
        <div
          style={{
            fontSize: 11.5,
            color: 'var(--ink-2)',
            lineHeight: 1.45,
            marginBottom: 4,
            fontStyle: 'italic',
          }}
        >
          ↳ {row.source_message_snippet}
          {row.source_message_snippet.length >= 200 && '…'}
        </div>
      )}

      {row.body && (
        <div
          style={{
            fontSize: 12,
            color: 'var(--ink-2)',
            lineHeight: 1.5,
            maxHeight: 60,
            overflow: 'hidden',
            position: 'relative',
            marginBottom: 6,
          }}
        >
          {row.body.slice(0, 240)}
          {row.body.length > 240 && '…'}
        </div>
      )}

      <button
        onClick={onShowHistory}
        className="mono"
        style={{
          fontSize: 10.5,
          padding: '3px 8px',
          border: '1px solid var(--rule)',
          borderRadius: 4,
          background: 'white',
          color: 'var(--ink-2)',
          fontWeight: 600,
          letterSpacing: '.04em',
          cursor: 'pointer',
        }}
      >
        {t('deleted.viewFullHistory')}
      </button>
    </article>
  );
}

function formatDeletedAt(iso: string): string {
  const d = new Date(iso);
  const now = new Date();
  const dayMs = 24 * 60 * 60 * 1000;
  const diff = (now.getTime() - d.getTime()) / dayMs;
  const hh = String(d.getHours()).padStart(2, '0');
  const mm = String(d.getMinutes()).padStart(2, '0');
  if (diff < 1 && d.getDate() === now.getDate()) return `${hh}:${mm}`;
  if (diff < 2) return `${hh}:${mm} · ${i18n.t('deleted.yesterday')}`;
  if (diff < 7) return `${hh}:${mm} · ${i18n.t('deleted.daysAgo', { count: Math.floor(diff) })}`;
  return d.toLocaleString('en-US', { month: 'short', day: 'numeric' });
}
